import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { FlowPrimaryButton } from "@/components/consultation-flow/ui";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, AlertTriangle } from "lucide-react";
import type { NumericInputNode as NumericInputNodeType } from "@/lib/pharmacyFirst/types";

export interface NumericInputAnswer {
  value: number;
  wasPrePopulated: boolean;
  plausibilityConfirmation: {
    message: string;
    confirmedByPharmacist: true;
  } | null;
}

interface NumericInputNodeProps {
  node: NumericInputNodeType;
  prePopulatedValue?: number | null;
  isDarkMode?: boolean;
  stepNumber?: number;
  onAnswer: (answer: NumericInputAnswer) => void;
}

type PlausibilityState =
  | { tier: "silent_accept" }
  | { tier: "hard_reject"; message: string }
  | { tier: "confirmation_prompt"; message: string; confirmed: boolean }
  | { tier: "none" };

function evaluatePlausibility(
  value: number,
  node: NumericInputNodeType
): PlausibilityState {
  if (value < node.hardMin || value > node.hardMax) {
    return {
      tier: "hard_reject",
      message: `Value must be between ${node.hardMin} and ${node.hardMax} ${node.unit}.`,
    };
  }

  if (node.plausibilityBands) {
    for (const band of node.plausibilityBands) {
      const aboveMin = band.min === undefined || value >= band.min;
      const belowMax = band.max === undefined || value <= band.max;
      if (aboveMin && belowMax) {
        if (band.tier === "hard_reject") {
          return {
            tier: "hard_reject",
            message: band.message ?? `Value ${value} is outside acceptable bounds.`,
          };
        }
        if (band.tier === "confirmation_prompt") {
          return {
            tier: "confirmation_prompt",
            message:
              band.message ??
              `${value} ${node.unit} is an unusual value. Please confirm this is correct.`,
            confirmed: false,
          };
        }
        if (band.tier === "silent_accept") {
          return { tier: "silent_accept" };
        }
      }
    }
  }

  return { tier: "silent_accept" };
}

function formatValue(value: number, decimals: number): string {
  return value.toFixed(decimals);
}

function parseInput(raw: string, decimals: number): number | null {
  if (raw.trim() === "") return null;
  const parsed = parseFloat(raw);
  if (isNaN(parsed)) return null;
  const factor = Math.pow(10, decimals);
  return Math.round(parsed * factor) / factor;
}

export function NumericInputNode({
  node,
  prePopulatedValue,
  isDarkMode = false,
  stepNumber,
  onAnswer,
}: NumericInputNodeProps) {
  const [rawInput, setRawInput] = useState<string>(
    prePopulatedValue !== null && prePopulatedValue !== undefined
      ? formatValue(prePopulatedValue, node.decimals)
      : ""
  );
  const [plausibility, setPlausibility] = useState<PlausibilityState>({
    tier: "none",
  });
  const [wasPrePopulated] = useState(
    prePopulatedValue !== null && prePopulatedValue !== undefined
  );
  const plausibilityComputedFor = useRef<string>("");

  useEffect(() => {
    const parsed = parseInput(rawInput, node.decimals);
    if (parsed === null) {
      setPlausibility({ tier: "none" });
    } else {
      const result = evaluatePlausibility(parsed, node);
      setPlausibility(result);
    }
    plausibilityComputedFor.current = rawInput;
  }, [rawInput]);

  const plausibilityIsFresh = plausibilityComputedFor.current === rawInput;

  const parsedValue = parseInput(rawInput, node.decimals);
  const hasValue = parsedValue !== null;

  const canContinue = (() => {
    if (!hasValue) return false;
    if (plausibility.tier === "hard_reject") return false;
    if (plausibility.tier === "none") return false;
    if (
      plausibility.tier === "confirmation_prompt" &&
      !plausibility.confirmed
    ) {
      return false;
    }
    return true;
  })();

  const handleConfirmPlausibility = () => {
    if (plausibility.tier !== "confirmation_prompt") return;
    setPlausibility({ ...plausibility, confirmed: true });
  };

  const handleContinue = () => {
    if (!hasValue || parsedValue === null) return;

    onAnswer({
      value: parsedValue,
      wasPrePopulated,
      plausibilityConfirmation:
        plausibility.tier === "confirmation_prompt" && plausibility.confirmed
          ? { message: plausibility.message, confirmedByPharmacist: true }
          : null,
    });
  };

  const cardClass = `border rounded-2xl p-4 ${isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white"}`;

  const inputBorderClass = (() => {
    if (rawInput.length >= 2 && plausibilityIsFresh && plausibility.tier === "hard_reject") {
      return isDarkMode ? "border-red-600" : "border-red-400";
    }
    if (
      rawInput.length >= 2 &&
      plausibilityIsFresh &&
      plausibility.tier === "confirmation_prompt" &&
      !plausibility.confirmed
    ) {
      return isDarkMode ? "border-amber-500" : "border-amber-400";
    }
    if (hasValue && plausibilityIsFresh && plausibility.tier === "silent_accept") {
      return isDarkMode ? "border-emerald-600" : "border-emerald-400";
    }
    return isDarkMode ? "border-gray-600" : "border-gray-300";
  })();

  return (
    <div className={cardClass}>
      <div className="flex items-center gap-3 mb-4">
        {stepNumber !== undefined && (
          <div
            className={`flex items-center justify-center w-6 h-6 rounded-full text-sm font-semibold flex-shrink-0 ${isDarkMode ? "bg-white text-black" : "bg-black text-white"}`}
          >
            {stepNumber}
          </div>
        )}
        <div className="flex-1">
          <h3 className={`text-sm font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
            {node.text}
          </h3>
          {node.hint && (
            <p className={`text-xs mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
              {node.hint}
            </p>
          )}
        </div>
      </div>

      {wasPrePopulated && (
        <div className={`mb-3 text-xs px-3 py-1.5 rounded-md border ${isDarkMode ? "border-blue-700 bg-blue-950/40 text-blue-300" : "border-blue-200 bg-blue-50 text-blue-800"}`}>
          Pre-filled from patient record — please confirm or update
        </div>
      )}

      <div className="flex items-center gap-2 mb-3">
        <input
          type="number"
          inputMode="decimal"
          step={node.decimals === 0 ? "1" : `0.${"0".repeat(node.decimals - 1)}1`}
          min={node.hardMin}
          max={node.hardMax}
          value={rawInput}
          onChange={(e) => setRawInput(e.target.value)}
          placeholder={`Enter ${node.unit}…`}
          aria-label={node.text}
          className={`flex-1 rounded-lg border-2 px-3 py-2.5 text-sm font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ${isDarkMode
            ? `bg-gray-700 text-white placeholder-gray-500 ${inputBorderClass}`
            : `bg-white text-gray-900 placeholder-gray-400 ${inputBorderClass}`
          }`}
        />
        <span className={`text-sm font-medium whitespace-nowrap ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
          {node.unit}
        </span>
      </div>

      <p className={`text-xs mb-3 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
        Valid range: {node.hardMin}–{node.hardMax} {node.unit}
        {node.decimals > 0 && ` (${node.decimals} decimal place${node.decimals > 1 ? "s" : ""})`}
      </p>

      {plausibility.tier === "hard_reject" && rawInput.length >= 2 && plausibilityIsFresh && (
        <Alert className="mb-3 border-red-200 bg-red-50 text-red-900">
          <AlertCircle className="h-4 w-4 text-red-600" />
          <AlertDescription>{plausibility.message}</AlertDescription>
        </Alert>
      )}

      {plausibility.tier === "confirmation_prompt" &&
        !plausibility.confirmed &&
        rawInput.length >= 2 &&
        plausibilityIsFresh && (
          <div className={`mb-3 border rounded-lg p-3 ${isDarkMode ? "border-amber-700 bg-amber-950/30" : "border-amber-200 bg-amber-50"}`}>
            <div className="flex items-start gap-2 mb-2">
              <AlertTriangle className={`h-4 w-4 flex-shrink-0 mt-0.5 ${isDarkMode ? "text-amber-400" : "text-amber-600"}`} />
              <p className={`text-sm ${isDarkMode ? "text-amber-200" : "text-amber-900"}`}>
                {plausibility.message}
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={handleConfirmPlausibility}
              className={isDarkMode ? "border-amber-600 text-amber-200 hover:bg-amber-900" : "border-amber-400 text-amber-900 hover:bg-amber-100"}
            >
              Confirm value is correct
            </Button>
          </div>
        )}

      {plausibility.tier === "confirmation_prompt" && plausibility.confirmed && (
        <div className={`mb-3 text-xs px-3 py-1.5 rounded-md border ${isDarkMode ? "border-amber-700 bg-amber-950/40 text-amber-300" : "border-amber-200 bg-amber-50 text-amber-800"}`}>
          Unusual value confirmed by pharmacist
        </div>
      )}

      <FlowPrimaryButton
        label="Continue"
        className="w-full"
        onClick={handleContinue}
        disabled={!canContinue}
        blockedReason={
          !hasValue
            ? "Enter a value to continue."
            : plausibility.tier === "confirmation_prompt"
              ? "Confirm the unusual value to continue."
              : "Enter a value within the allowed range to continue."
        }
      />
    </div>
  );
}
